import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

/** Título de seção é curto por definição — mesmo teto de `NoticiasPorTema`. */
const TITLE_RECOMMENDED_LENGTH = 60;

/**
 * Teto de cards, fixo — é o tamanho da lista de campaigns nacionais que o
 * bloco reproduz (ver /comunicacao/campaigns-nacionais).
 *
 * O `object_list` do Volto não tem `maxItems`, então o corte acontece na
 * `View` e o editor é avisado quando adiciona itens além disso.
 */
export const MAX_CAMPAIGNS = 5;

export interface CampaignRef {
  '@id'?: string;
  '@type'?: string;
  Title?: string;
  Description?: string;
  /** Chave da busca que monta o card interno — ver `useCampaigns`. */
  UID?: string;
}

export interface CampaignItem {
  href?: CampaignRef[];
  external?: boolean;
  /** Usados só quando o target é externo — ver `helpers.toCampaignCards`. */
  title?: string;
  description?: string;
  image?: string | CampaignRef[];
  alt?: string;
}

export interface CampaignsData {
  '@type'?: string;
  title?: string;
  items?: CampaignItem[];
}

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_campanhas_block',
    defaultMessage: 'Campanhas',
  },
  title: {
    id: 'mpf_campanhas_title',
    defaultMessage: 'Título da seção',
  },
  titleHelp: {
    id: 'mpf_campanhas_title_help',
    defaultMessage:
      'Cabeçalho exibido acima dos cards. Deixe em branco para não mostrar cabeçalho nenhum.',
  },
  titlePlaceholder: {
    id: 'mpf_campanhas_title_placeholder',
    defaultMessage: 'Ex.: Campanhas nacionais',
  },
  items: {
    id: 'mpf_campanhas_items',
    defaultMessage: 'Campanhas',
  },
  itemsHelp: {
    id: 'mpf_campanhas_items_help',
    defaultMessage:
      'Escolha as campanhas na ordem em que devem aparecer. São exibidas no máximo 5 — os itens seguintes ficam guardados, mas não vão para a página.',
  },
  item: {
    id: 'mpf_campanhas_item',
    defaultMessage: 'Campanha',
  },
  fieldsetExterna: {
    id: 'mpf_campanhas_fieldset_externa',
    defaultMessage: 'Campanha externa',
  },
  href: {
    id: 'mpf_campanhas_href',
    defaultMessage: 'Destino',
  },
  hrefHelp: {
    id: 'mpf_campanhas_href_help',
    defaultMessage:
      'Uma página de campanha do Portal MPF ou o endereço de um site próprio da campanha (ex.: contecomagente.mpf.mp.br). Escolhendo uma página do portal, o card usa o título, a descrição e a imagem dela — e se atualiza sozinho quando a página muda.',
  },
  external: {
    id: 'mpf_campanhas_external',
    defaultMessage: 'Abrir em nova aba',
  },
  externalHelp: {
    id: 'mpf_campanhas_external_help',
    defaultMessage:
      'Site externo já abre em nova aba por padrão; desmarque para abrir na mesma. Para página do portal, marque só se quiser forçar a nova aba.',
  },
  itemTitle: {
    id: 'mpf_campanhas_item_title',
    defaultMessage: 'Título da campanha',
  },
  itemTitleHelp: {
    id: 'mpf_campanhas_item_title_help',
    defaultMessage:
      'Obrigatório quando o destino é um site externo — é o texto que vira o link do card. Para página do portal, deixe em branco: o título vem de lá.',
  },
  itemDescription: {
    id: 'mpf_campanhas_item_description',
    defaultMessage: 'Descrição da campanha',
  },
  itemDescriptionHelp: {
    id: 'mpf_campanhas_item_description_help',
    defaultMessage:
      'Exibida em até três linhas, abaixo do título. Só é usada quando o destino é um site externo.',
  },
  itemImage: {
    id: 'mpf_campanhas_item_image',
    defaultMessage: 'Imagem da campanha',
  },
  itemImageHelp: {
    id: 'mpf_campanhas_item_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Só é usada quando o destino é um site externo — a campanha do portal usa a Imagem de prévia da própria página.',
  },
  itemAlt: {
    id: 'mpf_campanhas_item_alt',
    defaultMessage: 'Texto alternativo',
  },
  itemAltHelp: {
    id: 'mpf_campanhas_item_alt_help',
    defaultMessage:
      'Descreve a imagem para quem usa leitor de tela. Obrigatório ao enviar imagem própria (destino externo); a campanha do portal herda a imagem — e o texto alternativo — da própria página.',
  },
});

/**
 * Campos de uma campaign.
 *
 * O target aceita página do portal OU site externo, e é ele que decide de
 * onde vem o conteúdo do card:
 *
 * - página do portal → título, descrição e imagem saem do brain da página
 *   (nada é copiado para dentro do bloco, e editar a campaign atualiza o card);
 * - site externo → não há página para consultar, então os campos abaixo são a
 *   única fonte e o editor precisa preenchê-los.
 *
 * Os campos ficam num fieldset próprio, "Campanha externa", justamente para
 * dizer quando é que valem. O `ObjectListWidget` do Volto usa um schema só
 * para todos os itens da lista, então não dá para escondê-los por item — a
 * separação em fieldset é o mais perto disso sem inventar widget novo.
 */
const campaignItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['href', 'external'],
    },
    {
      id: 'externa',
      title: intl.formatMessage(messages.fieldsetExterna),
      fields: ['title', 'description', 'image', 'alt'],
    },
  ],
  properties: {
    href: {
      title: intl.formatMessage(messages.href),
      description: intl.formatMessage(messages.hrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // `UID` é o que o `useCampaigns` usa para buscar a campaign interna;
      // sem ele guardado aqui o card não tem como ser montado. Um target
      // externo simplesmente não tem UID, e aí valem os campos manuais.
      selectedItemAttrs: ['@id', '@type', 'Title', 'Description', 'UID'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.external),
      description: intl.formatMessage(messages.externalHelp),
      type: 'boolean',
    },
    title: {
      title: intl.formatMessage(messages.itemTitle),
      description: intl.formatMessage(messages.itemTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    description: {
      title: intl.formatMessage(messages.itemDescription),
      description: intl.formatMessage(messages.itemDescriptionHelp),
      widget: 'textarea',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL — mesmo campo do
    // bloco "Iniciativas e Projetos".
    image: {
      title: intl.formatMessage(messages.itemImage),
      description: intl.formatMessage(messages.itemImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.itemAlt),
      description: intl.formatMessage(messages.itemAltHelp),
    },
  },
  required: ['href'],
});

/** Ver `AcoesRapidas/schema.ts` sobre `activeObject`/`setActiveObject`. */
export const mpfCampanhasSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'items'],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: intl.formatMessage(messages.titlePlaceholder),
      widget: 'charCount',
      recommendedLength: TITLE_RECOMMENDED_LENGTH,
    },
    items: {
      title: intl.formatMessage(messages.items),
      description: intl.formatMessage(messages.itemsHelp),
      widget: 'object_list',
      schema: campaignItemSchema({ intl }),
      schemaExtender: requireAltWhenOwnImage,
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfCampanhasSchema;
